import { Dayjs } from "dayjs";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";
import { ClassCategory } from "shared/types/enum";
import { now } from "shared/utils/dayjs";

export interface ScheduleFilters {
  instructorIds: string[];
  customerIds: string[];
  classIds: string[];
  classTypes: ClassCategory[];
}

// The slot the admin clicked in the calendar. Single source of truth that seeds
// the creation panel's date/time; cleared when the panel closes.
export interface ScheduleCreateDraft {
  date: Dayjs;
  startTime: Dayjs;
}

// Live preview the creation panel publishes so the calendar can draw the ghost
// block. Driven by the panel's current form values, not just the initial click.
export interface ScheduleGhostPreview {
  date: Dayjs;
  startTime: Dayjs;
  durationMinutes: number;
  className?: string;
  instructorId?: string;
}

export interface ScheduleViewParams {
  date: string; // "YYYY-MM-DD"
  time: string | null; // "HH:mm" or null
}

export interface ScheduleState {
  filters: ScheduleFilters;
  view: ScheduleViewParams;
}

interface ScheduleContextValue {
  state: ScheduleState;
  setFilters: (updater: (prev: ScheduleFilters) => ScheduleFilters) => void;
  setView: (updater: (prev: ScheduleViewParams) => ScheduleViewParams) => void;
  isCreateDrawerOpen: boolean;
  setCreateDrawerOpen: (open: boolean) => void;
  // Click-to-create: the clicked slot (seeds the panel) and the live ghost
  // preview (drawn on the calendar while the panel is open).
  createDraft: ScheduleCreateDraft | null;
  setCreateDraft: (draft: ScheduleCreateDraft | null) => void;
  // One entry per selected recurrence day (or a single entry for a
  // non-recurring/single-day create) — see CreateScheduleDrawer.tsx.
  ghostPreviews: ScheduleGhostPreview[];
  setGhostPreviews: (previews: ScheduleGhostPreview[]) => void;
}

const ScheduleContext = createContext<ScheduleContextValue | null>(null);

const SESSION_STORAGE_KEY = "scheduleFilters";
const DEFAULT_CLASS_TYPES = [ClassCategory.Private, ClassCategory.Open];

// A reload should send Jadwal back to today ONCE per document load. The
// navigation timing entry describes the DOCUMENT (it still reads "reload"
// after any number of SPA navigations), so the first ScheduleProvider mount
// consumes the flag; pressing Back from a schedule detail later in the
// session remounts the provider but must NOT re-trigger the reset. A real
// reload re-evaluates this module, so the flag is naturally fresh per
// document. In-app navigation that sets `date` (e.g. a clash-notification
// deep link) is untouched, since those aren't browser reloads. Wrapped in
// try/catch since the Navigation Timing API, while broadly supported,
// isn't guaranteed.
let reloadResetPending: boolean | null = null;

const consumeReloadReset = () => {
  if (reloadResetPending === null) {
    try {
      const [entry] = performance.getEntriesByType(
        "navigation"
      ) as PerformanceNavigationTiming[];
      reloadResetPending = entry?.type === "reload";
    } catch {
      reloadResetPending = false;
    }
  }
  const pending = reloadResetPending;
  reloadResetPending = false;
  return pending;
};

const loadFiltersFromSessionStorage = (): ScheduleFilters => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        instructorIds: parsed.instructorIds ?? [],
        customerIds: parsed.customerIds ?? [],
        classIds: parsed.classIds ?? [],
        classTypes: parsed.classTypes ?? DEFAULT_CLASS_TYPES,
      };
    }
  } catch (error) {
    console.warn("Failed to load filters from sessionStorage:", error);
  }
  return {
    instructorIds: [],
    customerIds: [],
    classIds: [],
    classTypes: DEFAULT_CLASS_TYPES,
  };
};

const saveFiltersToSessionStorage = (filters: ScheduleFilters) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn("Failed to save filters to sessionStorage:", error);
  }
};

const sameGhostPreviews = (
  a: ScheduleGhostPreview[],
  b: ScheduleGhostPreview[],
): boolean =>
  a.length === b.length &&
  a.every((prev, i) => {
    const next = b[i];
    return (
      prev.date.valueOf() === next.date.valueOf() &&
      prev.startTime.valueOf() === next.startTime.valueOf() &&
      prev.durationMinutes === next.durationMinutes &&
      prev.className === next.className &&
      prev.instructorId === next.instructorId
    );
  });

export const ScheduleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<ScheduleCreateDraft | null>(
    null,
  );
  const [ghostPreviews, setGhostPreviewsState] = useState<
    ScheduleGhostPreview[]
  >([]);
  // The create panel republishes its previews on EVERY form change (typing a
  // note, picking a client), usually with the same content or another empty
  // list. Each new array changed this context and re-rendered the whole
  // calendar, so only a real change is let through: keeping the previous
  // array makes React skip the update.
  const setGhostPreviews = useCallback((next: ScheduleGhostPreview[]) => {
    setGhostPreviewsState((prev) =>
      sameGhostPreviews(prev, next) ? prev : next,
    );
  }, []);

  const getViewFromParams = (sp: URLSearchParams): ScheduleViewParams => {
    const urlDate = sp.get("date");
    const urlTime = sp.get("time");

    return {
      date: urlDate ?? now().format("YYYY-MM-DD"),
      time: urlTime ?? null,
    };
  };

  const [state, setState] = useState<ScheduleState>(() => ({
    filters: loadFiltersFromSessionStorage(),
    view: getViewFromParams(searchParams),
  }));

  const setFilters = (updater: (prev: ScheduleFilters) => ScheduleFilters) => {
    setState((prev) => ({ ...prev, filters: updater(prev.filters) }));
  };

  const setView = (
    updater: (prev: ScheduleViewParams) => ScheduleViewParams,
  ) => {
    const prevView = getViewFromParams(searchParams);
    const nextView = updater(prevView);

    const params = new URLSearchParams(searchParams);

    params.set("date", nextView.date);

    if (nextView.time) params.set("time", nextView.time);
    else params.delete("time");

    setSearchParams(params, { replace: true });
  };

  // A reload leaves whatever date/time the admin last navigated to sitting
  // in the URL. Correct it back to today once so refreshing the Jadwal page
  // returns to the live week; consume-once semantics keep back-navigation
  // from a schedule detail on the week the admin was viewing.
  useEffect(() => {
    if (!consumeReloadReset()) return;
    const todayStr = now().format("YYYY-MM-DD");
    if (searchParams.get("date") === todayStr && !searchParams.get("time")) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.set("date", todayStr);
    params.delete("time");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update filters to session storage
  useEffect(() => {
    saveFiltersToSessionStorage(state.filters);
  }, [state.filters]);

  // update the view when the search params change
  useEffect(() => {
    setState((prev) => ({ ...prev, view: getViewFromParams(searchParams) }));
  }, [searchParams]);

  // NOTE: no "close drawer on mount" effect here — isCreateDrawerOpen already
  // initializes false, and such a reset would run AFTER child effects on
  // mount, stomping the dashboard quick action that opens the create panel
  // on arrival (useOpenCreateFromNav in ScheduleContent).

  const value = useMemo(
    () => ({
      state,
      setFilters,
      setView,
      isCreateDrawerOpen,
      setCreateDrawerOpen,
      createDraft,
      setCreateDraft,
      ghostPreviews,
      setGhostPreviews,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, isCreateDrawerOpen, createDraft, ghostPreviews],
  );

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export const useScheduleState = () => {
  const ctx = useContext(ScheduleContext);
  if (!ctx) {
    throw new Error("useScheduleState must be used within ScheduleProvider");
  }
  return ctx;
};

// For components rendered both inside and outside the calendar's ScheduleProvider
// (e.g. ActionedSchedules in the list view). Returns null if no provider is in
// the tree, letting the caller pick a sensible default.
export const useScheduleStateOptional = () => useContext(ScheduleContext);
